import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';

import { 
    getServiceUsersForServerAPI, 
    getServerDetailsAPI, 
    toggleUserStatusAPI,
    removeUserFromServerAPI,
    deleteUserAPI,
    bulkCloneServiceUsersAPI,
    fetchServersAPI
} from '../services/serverService';
import AddServiceUserModal from '../components/AddServiceUserModal';
import BulkUploadModal from '../components/BulkUploadModal';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';

const ServiceUserListPage = () => {
    const { userRole } = useAuth();
    const { serverId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Core Data States
    const [serviceUsers, setServiceUsers] = useState([]);
    const [serverName, setServerName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [modalState, setModalState] = useState(null);

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsersCount, setTotalUsersCount] = useState(0);

    // Bulk Action States
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [isCloning, setIsCloning] = useState(false);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [availableServers, setAvailableServers] = useState([]);
    const [targetServerId, setTargetServerId] = useState('');
    const [showBulkUpload, setShowBulkUpload] = useState(false);

    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');
    const canDelete = hasPermission(userRole, 'CAN_DELETE_DATA');

    useEffect(() => {
        if (location.state?.openAddModal) setModalState('add');
    }, [location.state]);

    // Fetch data 
    const fetchAllData = async (page = currentPage) => {
        try {
            setIsLoading(true);
            const [serviceUserResponse, serverResponse] = await Promise.all([
                getServiceUsersForServerAPI(serverId, page, 50),
                getServerDetailsAPI(serverId)
            ]);
            
            const responseData = serviceUserResponse.data?.data;
            
            let users = [];
            let total = 0;
            let fetchedTotalPages = 1;

            if (Array.isArray(responseData)) {
                users = responseData;
                total = responseData.length;
            } else if (responseData && responseData.users) {
                users = responseData.users;
                total = responseData.total || 0;
                fetchedTotalPages = responseData.totalPages || 1;
            }
            
            setServiceUsers(users || []); 
            setTotalUsersCount(total);
            setTotalPages(fetchedTotalPages);
            setServerName(serverResponse.data?.data?.companyName || 'Unknown Server');
            setSelectedUserIds([]); 
            
        } catch (error) {
            console.error("Failed to fetch page data", error);
            setServiceUsers([]); 
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!serverId) return;
        fetchAllData(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId, currentPage]);

    const handleCloseModal = () => setModalState(null);

    // --- CHECKBOX LOGIC ---
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const visibleIds = serviceUsers.map(u => u.username || u.id);
            setSelectedUserIds(visibleIds);
        } else {
            setSelectedUserIds([]);
        }
    };

    const handleSelectRow = (userId) => {
        setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    };

    // --- CLONE MODAL LOGIC ---
    const handleOpenCloneModal = async () => {
        if (selectedUserIds.length === 0) return alert("Select at least one user to clone.");
        setIsLoading(true);
        try {
            const response = await fetchServersAPI();
            
            // 1. DEFENSIVE EXTRACTION: Find the array no matter how it is nested
            let serversArray = [];
            const payload = response.data;
            
            if (Array.isArray(payload)) serversArray = payload;
            else if (payload?.data && Array.isArray(payload.data)) serversArray = payload.data;
            else if (payload?.servers && Array.isArray(payload.servers)) serversArray = payload.servers;
            else if (payload?.data?.servers && Array.isArray(payload.data.servers)) serversArray = payload.data.servers;

            if (!Array.isArray(serversArray)) {
                console.error("Unrecognized server payload:", response);
                throw new Error("Could not parse server list array.");
            }

            // 2. Safely filter out the current server (handling both 'id' and 'server_id' keys)
            const servers = serversArray.filter(s => (s.server_id || s.id) !== serverId);
            setAvailableServers(servers);
            setTargetServerId('');
            setShowCloneModal(true);
        } catch (error) {
            console.error("Clone Modal Error:", error);
            alert("Failed to load server list. Check developer console.");
        } finally {
            setIsLoading(false);
        }
    };

    const executeClone = async () => {
        if (!targetServerId) return alert("Please select a target server.");
        
        const confirmClone = window.confirm(`Clone ${selectedUserIds.length} users to selected server?\n\nProceed?`);
        if (!confirmClone) return;

        setIsCloning(true);
        setShowCloneModal(false); 
        
        try {
            const response = await bulkCloneServiceUsersAPI({ targetServerId, sourceServerId: serverId, userIds: selectedUserIds });
            const { successCount, clonedUsers, errors } = response.data.data;

            if (successCount > 0) {
                alert(`Successfully cloned ${successCount} users. Downloading credentials CSV...`);
                
                const headers = ['Full Name', 'New Username', 'Email', 'Temporary Password'];
                const rows = clonedUsers.map(u => [`"${u.fullName}"`, `"${u.newUsername}"`, `"${u.email}"`, `"${u.plainTextPassword}"`]);
                const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join("\n");
                
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `Cloned_Users_Credentials_${targetServerId}.csv`;
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            if (errors && errors.length > 0) alert(`Warning: ${errors.length} users failed to clone. Check console.`);
            setSelectedUserIds([]);
        } catch (error) {
            alert("Bulk cloning failed: " + (error.message || "Internal Error"));
        } finally {
            setIsCloning(false);
        }
    };

    // --- SINGLE ACTIONS ---
    const handleToggleStatus = async (user) => {
        const isActiveUser = user.active !== undefined ? user.active === true : user.status === true;
        const reason = window.prompt(`Reason to ${isActiveUser ? "disable" : "enable"} ${user.fullName}? (Required):`);
        if (!reason || reason.trim() === "") return;
        try {
            const targetId = user.username || user.id || user.uid || user.email;
            await toggleUserStatusAPI({ userId: targetId, uid: user.uid || targetId, serverId, role: 'serviceUser', reason: reason.trim() });
            fetchAllData();
        } catch (error) { alert("Could not update status."); }
    };

    const handleRemoveUser = async (user) => {
        const reason = window.prompt(`Reason to unlink ${user.fullName}? (Required):`);
        if (!reason || reason.trim() === "") return;
        try {
            const targetId = user.username || user.id || user.uid || user.email;
            await removeUserFromServerAPI({ userId: targetId, serverId, role: 'serviceUser', reason: reason.trim() });
            fetchAllData();
        } catch (error) { alert("Could not remove user."); }
    };

    const handleDeleteUser = async (user) => {
        const reason = window.prompt(`PERMANENTLY DELETE ${user.fullName}. Reason required:`);
        if (!reason || reason.trim() === "") return;
        try {
            const targetId = user.username || user.id || user.uid || user.email;
            await deleteUserAPI({ userId: targetId, serverId, role: 'serviceUser', reason: reason.trim() });
            fetchAllData();
        } catch (error) { alert("Error deleting user: " + error.message); }
    };

    const renderCloneModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', width: '400px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, color: '#2d3748' }}>Select Destination Server</h3>
                <p style={{ color: '#718096', fontSize: '14px', marginBottom: '20px' }}>Choose where to clone the {selectedUserIds.length} selected users.</p>
                
                <select 
                    value={targetServerId}
                    onChange={(e) => setTargetServerId(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0', marginBottom: '20px', fontSize: '15px' }}
                >
                    <option value="" disabled>-- Select a Server --</option>
                    {availableServers.map(s => {
                        const sId = s.server_id || s.id;
                        const sName = s.companyName || s.server_name || s.name || sId;
                        return (
                            <option key={sId} value={sId}>{sName}</option>
                        );
                    })}
                </select>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button onClick={() => setShowCloneModal(false)} style={{ padding: '8px 16px', border: '1px solid #cbd5e0', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={executeClone} disabled={!targetServerId} style={{ padding: '8px 16px', border: 'none', background: targetServerId ? '#3182ce' : '#a0aec0', color: 'white', borderRadius: '4px', cursor: targetServerId ? 'pointer' : 'not-allowed', fontWeight: 'bold' }}>Confirm Clone</button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-container">
            {modalState && (
                <AddServiceUserModal serverId={serverId} closeModal={handleCloseModal} refreshList={fetchAllData} userToEdit={modalState !== 'add' ? modalState : null} existingUsers={serviceUsers} />
            )}
            
            {showCloneModal && renderCloneModal()}

            {showBulkUpload && (
                <BulkUploadModal 
                    serverId={serverId} 
                    role="serviceUser"
                    closeModal={() => setShowBulkUpload(false)} 
                    refreshList={fetchAllData} 
                />
            )}
            
            <div className="page-header">
                <div>
                    <h1 style={{ margin: 0 }}>Service Users List for <span style={{ color: 'var(--primary-blue)' }}>{serverName}</span></h1>
                    <p style={{ margin: '5px 0 0', color: '#718096', fontWeight: 'bold' }}>Total Users: {totalUsersCount}</p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {canEdit && (
                        <>
                            <button className="add-button" onClick={() => setModalState('add')}>+ Add New Service User</button>
                            <button className="add-button" onClick={() => setShowBulkUpload(true)} style={{ backgroundColor: '#2b6cb0' }}>Bulk Import via CSV</button>
                        </>
                    )}
                </div>
            </div>

            <Link to={`/servers/${serverId}`} className="back-link" style={{ marginBottom: '1rem' }}>← Back to Server Details</Link>

            {selectedUserIds.length > 0 && canDelete && (
                <div style={{ backgroundColor: '#ebf8ff', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #bee3f8' }}>
                    <div style={{ color: '#2b6cb0', fontWeight: 'bold' }}>{selectedUserIds.length} user(s) selected on this page</div>
                    <button onClick={handleOpenCloneModal} disabled={isCloning} style={{ backgroundColor: '#3182ce', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: 'bold', cursor: isCloning ? 'not-allowed' : 'pointer' }}>
                        {isCloning ? 'Cloning & Generating Passwords...' : 'Clone Selected to New Server'}
                    </button>
                </div>
            )}

            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th style={{ width: '60px', textAlign: 'center', padding: '0 10px', overflow: 'visible', textOverflow: 'clip' }}>
                                <input 
                                    type="checkbox" 
                                    onChange={handleSelectAll} 
                                    checked={serviceUsers.length > 0 && selectedUserIds.length === serviceUsers.length}
                                    style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                />
                            </th>
                            <th>Username</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="7" className="no-data-cell">Loading Service Users...</td></tr>
                        ) : serviceUsers.length > 0 ? (
                            serviceUsers.map((user) => {
                                const isActiveUser = user.active !== undefined ? user.active === true : user.status === true;
                                const targetId = user.username || user.id;

                                return (
                                    <tr key={user.id} onClick={() => navigate(`/servers/${serverId}/service-users/${user.id}`)} style={{ cursor: 'pointer', backgroundColor: selectedUserIds.includes(targetId) ? '#f0f9ff' : 'transparent' }}>
                                        <td style={{ textAlign: 'center', padding: '0 10px', overflow: 'visible', textOverflow: 'clip' }} onClick={(e) => e.stopPropagation()}>
                                            <input 
                                                type="checkbox"
                                                checked={selectedUserIds.includes(targetId)}
                                                onChange={() => handleSelectRow(targetId)}
                                                style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                            />
                                        </td>
                                        <td style={{ fontWeight: '500' }}>{user.username}</td>
                                        <td>{user.fullName}</td>
                                        <td>{user.email}</td>
                                        <td>{user.phoneNumber}</td>
                                        <td><span className={isActiveUser ? 'status-active' : 'status-inactive'}>{isActiveUser ? 'Active' : 'Suspended'}</span></td>
                                        <td className="actions-cell">
                                            {canEdit ? (
                                                <div className="actions-wrapper" onClick={(e) => e.stopPropagation()}>
                                                    <button className="action-button edit" onClick={() => setModalState(user)}>Edit</button>
                                                    <button className={`action-button ${isActiveUser ? 'danger' : 'success'}`} onClick={() => handleToggleStatus(user)}>{isActiveUser ? 'Disable' : 'Enable'}</button>
                                                    
                                                    {canDelete && (
                                                        <>
                                                            <button className="action-button" style={{ backgroundColor: '#f0ad4e', color: 'white', borderColor: '#eea236' }} onClick={() => handleRemoveUser(user)} title="Remove from this server only">Unlink</button>
                                                            <button className="action-button delete" onClick={() => handleDeleteUser(user)} title="Permanently Delete">Delete</button>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>View Only</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="7" className="no-data-cell">No Service Users Found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!isLoading && totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <button 
                        disabled={currentPage === 1} 
                        onClick={() => setCurrentPage(prev => prev - 1)}
                        style={{ padding: '8px 16px', border: '1px solid #cbd5e0', borderRadius: '4px', backgroundColor: currentPage === 1 ? '#f7fafc' : 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 'bold', color: '#4a5568' }}
                    >
                        ← Previous
                    </button>
                    
                    <span style={{ fontWeight: 'bold', color: '#4a5568' }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    
                    <button 
                        disabled={currentPage === totalPages} 
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        style={{ padding: '8px 16px', border: '1px solid #cbd5e0', borderRadius: '4px', backgroundColor: currentPage === totalPages ? '#f7fafc' : 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: 'bold', color: '#4a5568' }}
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
    );
};

export default ServiceUserListPage;